/*
 * Build metadata card: pure model, no React. The values derived from a build for its optional rows (EAS profile,
 * cost, toolchain, provider metadata) and the labels that depend on the platform or on how the build ended.
 */
import type { Platform } from '@hangar/shared';
import type { Build, BuildEvent } from '@/api/schemas';
import { formatDateTime, shortSha, timeUntil } from '@/lib/format';
import { detailValue } from './checksModel';

/** The profile of the app's build configuration (`eas.json`) the build used, shown after the Hangar profile. */
export function easProfileOf(b: Pick<Build, 'provider'>): string | undefined {
  const meta = b.provider?.meta;
  return meta && typeof meta.profile === 'string' ? meta.profile : undefined;
}

/** The estimated cost with two decimals, or `null` when the API sent none. */
export const estimatedCostText = (b: Pick<Build, 'estimatedCostUsd'>): string | null => (b.estimatedCostUsd !== null && b.estimatedCostUsd !== undefined ? b.estimatedCostUsd.toFixed(2) : null);

/** `node 20 · xcode 16` from the toolchain map, or `null` when it is missing or empty. */
export function toolchainText(b: Pick<Build, 'toolchain'>): string | null {
  const entries = Object.entries(b.toolchain ?? {});
  return entries.length > 0 ? entries.map(([k, v]) => `${k} ${v}`).join(' · ') : null;
}

/** The label of the version row: iOS has a build number, Android a versionCode. */
export const versionRowLabel = (platform: Platform): string => (platform === 'ios' ? 'Version · build number' : 'Version · versionCode');

/** A build that finished shows when; one still going shows how long it has left before Hangar stops it. */
export const endRowLabel = (b: Pick<Build, 'finishedAt'>): 'Finished' | 'Time limit' => (b.finishedAt ? 'Finished' : 'Time limit');

/** "1 h 32 min left" until the deadline, "Time is up" past it. */
export function deadlineText(deadlineAt: string, now: number): string {
  const left = timeUntil(deadlineAt, now);
  return left === 'expired' ? 'Time is up' : `${left} left`;
}

/**
 * When work on the build started: the earliest of its first phase (Hangar fetching the code), the runner's start and the
 * provider's start. `startedAt` alone stays empty while Hangar is still preparing the build, which read as "not started".
 */
export function startedAtOf(b: Pick<Build, 'startedAt' | 'provider'>, events: Pick<BuildEvent, 'kind' | 'createdAt'>[] | undefined): string | null {
  const runner = b.provider?.meta?.runner as { startedAt?: unknown } | undefined;
  const firstPhase = events?.find((e) => e.kind === 'phase_started')?.createdAt;
  const candidates = [firstPhase, typeof runner?.startedAt === 'string' ? runner.startedAt : undefined, b.startedAt ?? undefined].filter((t): t is string => Boolean(t));
  return candidates.length > 0 ? candidates.reduce((a, c) => (new Date(c).getTime() < new Date(a).getTime() ? c : a)) : null;
}

const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** A value of the advanced details: timestamps readable, the rest as text. */
const advancedValue = (v: unknown): string => (typeof v === 'string' && ISO_RE.test(v) ? formatDateTime(v) : detailValue(v));

/**
 * The advanced details (collapsed by default): the provider's build id and raw status, then its metadata with nested
 * objects flattened one level ("runner.repository") and timestamps made readable (the build id is listed once).
 */
export function advancedRows(b: Pick<Build, 'provider'>): [string, string][] {
  const rows: [string, string][] = [];
  if (b.provider?.buildId) rows.push(['build id', b.provider.buildId]);
  if (b.provider?.statusRaw) rows.push(['service status', b.provider.statusRaw]);
  for (const [k, v] of Object.entries(b.provider?.meta ?? {})) {
    if (k === 'providerBuildId' && v === b.provider?.buildId) continue;
    if (v && typeof v === 'object' && !Array.isArray(v)) for (const [k2, v2] of Object.entries(v)) rows.push([`${k}.${k2}`, advancedValue(v2)]);
    else rows.push([k, advancedValue(v)]);
  }
  return rows;
}

/**
 * What Hangar did for a `hangar_runner` build, in plain words: it takes the commit from the app's repository and sends
 * it to the build service (`provider.meta.runner`). Null for other builds (the CI ran it, or the service was asked).
 */
export function runnerLineOf(b: Pick<Build, 'executionMode' | 'provider' | 'git'>): string | null {
  if (b.executionMode !== 'hangar_runner') return null;
  const runner = (b.provider?.meta?.runner ?? {}) as { repository?: unknown; commit?: unknown; installed?: unknown; finishedAt?: unknown };
  const repository = typeof runner.repository === 'string' ? runner.repository : "the app's repository";
  const commit = shortSha(typeof runner.commit === 'string' ? runner.commit : b.git?.commitSha);
  const what = commit ? `commit ${commit}` : 'the latest commit';
  if (runner.finishedAt === undefined) return `Hangar takes ${what} from ${repository} and sends it to the build service`;
  return `Hangar took ${what} from ${repository}${runner.installed === true ? ', installed its dependencies' : ''} and sent it to the build service`;
}
